import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';

import { VisuItemLabel } from '../../common/models/label.model';

/**
 * Possible durations of a time step. The user can choose a time step only among those
 * available in this array.
 */
export const TIME_STEP_DURATION_IN_MN = [
  5, 10, 15, 20, 30, 60, 120, 180, 240, 360, 480, 720
];

export const CELL_HEIGHT = 64; // px
export const CELL_MIN_WIDTH = 80; // px
export const CELL_LEGEND_WIDTH = 150; // px;
/** Narrowest a block is drawn, so an instantaneous step still shows. */
export const MIN_TASK_WIDTH_PX = 4;

export type TaskKind =
  | 'work'
  | 'drive'
  | 'start'
  | 'finish'
  | 'pause'
  | 'home-rest'
  | 'wait'
  | 'setup'
  | 'setdown';
export type TaskType = 'basic' | 'pickup' | 'delivery' | 'delivery-pickup';

export interface Task {
  _dragAndDropped?: boolean;
  _leftPos?: number;
  _width?: number;
  labels: VisuItemLabel[];
  endDate: Date;
  highlightAggregatorIds: string[];
  id: number;
  kind: TaskKind;
  type: TaskType;
  name: string;
  startDate: Date;
  temperatureUids?: string[];
  siteUid: string;
  /** IANA zone of the task's site; absent when the site is not among the auxiliary data. */
  siteTimeZone?: string;
  colorSegments?: Array<{ color: string; percentage: number }>;
  temperatureSegments?: Array<{
    temperatureUid: string;
    percentage: number;
    color?: string;
    /** The placements behind this segment carry nothing, so its line is stroked dashed. */
    dashed?: boolean;
  }>;
  compartmentUids?: string[];
  dashedLine?: boolean;
  lazyProps?: {
    isDeliveryPickup: boolean;
    lineColor?: string;
    color: string;
  };
  /** On tasks that map to a real route-action step, and on the start marker, whose route can be edited. */
  routeUid?: string;
  actionUid?: string;
  stepIndex?: number;
  /** 0-based sequential number of this step among the route's rendered steps; the displayed step number. */
  actionIndex?: number;
  /** Jobs handled at this action, deduped. Absent on synthetic start/finish markers. */
  jobUids?: string[];
}

export interface TimelineRow {
  /** Hex color value for the route (e.g., '#ff0000') */
  color: string;
  endDate: Date;
  labels: VisuItemLabel[];
  lockCfg?: string;
  /** The raw route the row was drawn from, so gestures are computed from what is shown. */
  route?: UserRouteV1Model;
  validated?: boolean;
  name: string;
  startDate: Date;
  tasks: Task[];
  uid: string; // id of the primary aggregator, used for color
}

export interface TimelineState {
  /** Ending date of the data set. */
  dataEndDate: Date;
  /** Starting date of the data set. */
  dataStartDate: Date;
  /**
   * Ending date of the timeline (not possible to go forward - after 23:59) We only care
   * about the date (we don't consider the time).
   */
  endDate: Date;
  /** Number of time steps between startDate and endDate. */
  nbOfTimeSteps: number;
  /** Number of rows on screen */
  nbOfVisibleRows: number;
  /**
   * Number of time steps per day chosen by the user. Default value is 4 which is a time
   * step of 30mn. Values from 0 to TIME_STEPS_PER_DAY.length - 1.
   */
  nbTimeStepsPerDayPos: number;
  /** Rows displayed in the timeline. */
  rows: TimelineRow[];
  /**
   * Starting date of the timeline (not possible to go backward - before 0:00). We only care
   * about the date (we don't consider the time).
   */
  startDate: Date;
}

export const initialTimelineState: TimelineState = {
  dataStartDate: new Date(),
  dataEndDate: new Date(),
  startDate: new Date(),
  endDate: new Date(),
  nbOfTimeSteps: 0,
  nbOfVisibleRows: 0,
  nbTimeStepsPerDayPos: 4,
  rows: []
};
